import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tabs, Button, Slider, Input, Empty, Tooltip, message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faPause,
  faBackward,
  faForward,
  faVolumeUp,
  faVolumeMute,
  faBolt,
  faUser,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import VoiceCallService from '../../../services/VoiceCallService';
import {
  PlayerModal,
  PlayerHeader,
  WaveformContainer,
  PlayerControls,
  SpeedControls,
  PlayerTabs,
  TranscriptContainer,
  TranscriptEntry
} from '../styled';

// Format duration
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Generate waveform bars
const generateWaveform = (count = 50) => {
  return Array.from({ length: count }, () => Math.random() * 40 + 10);
};

const RecordingPlayerModal = ({ open, call, onClose }) => {
  // Audio state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  
  // Waveform
  const [waveformBars, setWaveformBars] = useState([]);
  
  // Transcript
  const [transcript, setTranscript] = useState([]);
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState(-1);
  
  // Notes
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  
  // Active tab
  const [activeTab, setActiveTab] = useState('transcript');

  // Initialize waveform
  useEffect(() => {
    setWaveformBars(generateWaveform(60));
  }, []);

  // Fetch recording URL when modal opens
  useEffect(() => {
    if (open && call?._id) {
      fetchRecording();
      fetchTranscript();
    }
    
    // Reset state when closed
    if (!open) {
      setIsPlaying(false);
      setCurrentTime(0);
      setAudioUrl(null);
      setError(null);
    }
  }, [open, call]);

  const fetchRecording = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await VoiceCallService.getCallRecording(call._id);
      
      if (response.success && response.url) {
        setAudioUrl(response.url);
        setDuration(call.duration || 0);
      } else {
        setError('Recording not available');
      }
    } catch (err) {
      console.error('Error fetching recording:', err);
      setError('Failed to load recording');
    } finally {
      setLoading(false);
    }
  };

  const fetchTranscript = async () => {
    try {
      const response = await VoiceCallService.getCallTranscript(call._id);
      if (response.success) {
        setTranscript(response.transcript || response.data || []);
      }
    } catch (err) {
      console.error('Error fetching transcript:', err);
    }
  };

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      
      // Update active transcript entry
      const index = transcript.findIndex((entry, i) => {
        const nextEntry = transcript[i + 1];
        const startTime = entry.timestamp || 0;
        const endTime = nextEntry?.timestamp || duration;
        return audio.currentTime >= startTime && audio.currentTime < endTime;
      });
      setActiveTranscriptIndex(index);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      setError('Failed to play audio');
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [transcript, duration]);

  // Play/Pause
  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(err => {
        console.error('Playback error:', err);
        setError('Failed to play audio');
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, audioUrl]);

  // Seek
  const handleSeek = useCallback((value) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.currentTime = value;
    setCurrentTime(value);
  }, []);

  // Skip forward/backward
  const skip = useCallback((seconds) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const newTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    if (isMuted) {
      audio.volume = volume || 1;
      setIsMuted(false);
    } else {
      audio.volume = 0;
      setIsMuted(true);
    }
  }, [isMuted, volume]);

  // Playback speed
  const handleSpeedChange = useCallback((speed) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.playbackRate = speed;
    setPlaybackRate(speed);
  }, []);

  // Jump to transcript timestamp
  const jumpToTimestamp = useCallback((timestamp) => {
    handleSeek(timestamp);
    if (!isPlaying) {
      togglePlay();
    }
  }, [handleSeek, isPlaying, togglePlay]);

  // Add note
  const addNote = useCallback(() => {
    if (!newNote.trim()) return;
    
    const note = {
      id: Date.now(),
      text: newNote,
      timestamp: currentTime,
      createdAt: new Date().toISOString()
    };
    
    setNotes(prev => [...prev, note]);
    setNewNote('');
    message.success('Note added');
    
    // TODO: Save note to backend
    // VoiceCallService.addCallNote(call._id, note);
  }, [newNote, currentTime]);

  // Download recording
  const downloadRecording = useCallback(async () => {
    if (!audioUrl) return;
    
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-recording-${call.callSid || call._id}.mp3`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error('Failed to download recording');
    }
  }, [audioUrl, call]);

  // Calculate waveform progress
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!call) return null;

  return (
    <PlayerModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      title={null}
      destroyOnClose
    >
      {/* Hidden audio element */}
      {audioUrl && (
        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      )}

      {/* Player Header */}
      <PlayerHeader>
        <div className="player-info">
          <div className="player-avatar">
            <FontAwesomeIcon icon={faBolt} />
          </div>
          <div className="player-details">
            <div className="player-name">{call.patientName || 'Unknown Caller'}</div>
            <div className="player-meta">
              {moment(call.initiatedAt).format('MMM D, YYYY h:mm A')}
            </div>
          </div>
          <div className="player-duration">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        {/* Waveform Visualization */}
        <WaveformContainer onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const percent = x / rect.width;
          handleSeek(percent * duration);
        }}>
          {waveformBars.map((height, i) => {
            const barProgress = (i / waveformBars.length) * 100;
            return (
              <div
                key={i}
                className={`waveform-bar ${barProgress < progress ? 'played' : ''} ${
                  Math.abs(barProgress - progress) < 2 ? 'current' : ''
                }`}
                style={{ height: `${height}%` }}
              />
            );
          })}
        </WaveformContainer>
      </PlayerHeader>

      {/* Player Controls */}
      <PlayerControls>
        <Tooltip title="Back 10s">
          <button className="control-btn" onClick={() => skip(-10)}>
            <FontAwesomeIcon icon={faBackward} />
          </button>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <button 
            className="control-btn play-btn" 
            onClick={togglePlay}
            disabled={loading || !!error}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>
        </Tooltip>

        <Tooltip title="Forward 10s">
          <button className="control-btn" onClick={() => skip(10)}>
            <FontAwesomeIcon icon={faForward} />
          </button>
        </Tooltip>

        <div className="progress-container">
          <span className="progress-time">{formatTime(currentTime)}</span>
          <Slider
            min={0}
            max={duration || 100}
            value={currentTime}
            onChange={handleSeek}
            tooltip={{ formatter: (v) => formatTime(v) }}
          />
          <span className="progress-time">{formatTime(duration)}</span>
        </div>

        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
          <button className="control-btn" onClick={toggleMute}>
            <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
          </button>
        </Tooltip>

        <Tooltip title="Download">
          <button className="control-btn" onClick={downloadRecording}>
            <FontAwesomeIcon icon={faDownload} />
          </button>
        </Tooltip>
      </PlayerControls>

      {/* Speed Controls */}
      <SpeedControls>
        <span className="speed-label">Speed:</span>
        {[0.5, 1, 1.5, 2].map(speed => (
          <button
            key={speed}
            className={`speed-btn ${playbackRate === speed ? 'active' : ''}`}
            onClick={() => handleSpeedChange(speed)}
          >
            {speed}x
          </button>
        ))}
      </SpeedControls>

      {/* Tabs: Transcript & Notes */}
      <PlayerTabs>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'transcript',
              label: 'Transcript',
              children: (
                <TranscriptContainer>
                  {transcript.length > 0 ? (
                    transcript.map((entry, index) => (
                      <TranscriptEntry 
                        key={index} 
                        $speaker={entry.speaker}
                        style={{
                          background: index === activeTranscriptIndex ? 'rgba(24, 144, 255, 0.05)' : undefined
                        }}
                      >
                        <div className="speaker-avatar">
                          <FontAwesomeIcon icon={entry.speaker === 'agent' ? faBolt : faUser} />
                        </div>
                        <div className="transcript-content">
                          <div className="speaker-name">
                            {entry.speaker === 'agent' ? 'AI Agent' : 'Patient'}
                          </div>
                          <div className="transcript-text">{entry.text}</div>
                        </div>
                        {entry.timestamp !== undefined && (
                          <Tooltip title="Jump to this moment">
                            <div 
                              className="transcript-time"
                              onClick={() => jumpToTimestamp(entry.timestamp)}
                            >
                              {formatTime(entry.timestamp)}
                            </div>
                          </Tooltip>
                        )}
                      </TranscriptEntry>
                    ))
                  ) : (
                    <Empty 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No transcript available"
                    />
                  )}
                </TranscriptContainer>
              )
            },
            {
              key: 'notes',
              label: 'Notes',
              children: (
                <div>
                  <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                    <Input
                      placeholder="Add a note at current time..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onPressEnter={addNote}
                    />
                    <Button type="primary" onClick={addNote}>
                      Add
                    </Button>
                  </div>
                  
                  {notes.length > 0 ? (
                    notes.map(note => (
                      <div 
                        key={note.id}
                        style={{
                          padding: 12,
                          background: '#fafafa',
                          borderRadius: 8,
                          marginBottom: 8
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span 
                            style={{ color: '#1890ff', cursor: 'pointer', fontSize: 12 }}
                            onClick={() => jumpToTimestamp(note.timestamp)}
                          >
                            {formatTime(note.timestamp)}
                          </span>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {moment(note.createdAt).format('h:mm A')}
                          </span>
                        </div>
                        <div>{note.text}</div>
                      </div>
                    ))
                  ) : (
                    <Empty 
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No notes yet"
                    />
                  )}
                </div>
              )
            }
          ]}
        />
      </PlayerTabs>
    </PlayerModal>
  );
};

export default RecordingPlayerModal;
